'use client';

import ProductListSection from '@/components/ProductListSection';
import { Category } from '@/models/CategoryDetail';
import { Product } from '@/models/Product';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import DotLoader, { DotEllipsis } from './ui/DotLoader';

type Props = {
  title: string;
  slug: string;
  /** Upstream getSlug slug when different from URL slug (e.g. urunler + locationSlug for /bolge). */
  apiSlug?: string;
  locationSlug?: string;
  category?: Category | null;
  initial: Product[];
  total: number;
  sort?: string;
  filters?: string;
  pageSize?: number;
  showTitle?: boolean;
  endpoint?: string;

  initialOffset?: number;
  initialPrevOffset?: number | null;
  initialFirstPage?: number;
  initialCurrentPage?: number;

  /** ProductListSection — araç süsleme landing ile aynı geniş kart/grid */
  productGridClassName?: string;
  productGridItemClassName?: string;
  cardMaxWidth?: number;
  cardClassName?: string;
  cardImageClassName?: string;
};

const pageVariableName = 'sayfa';

function makeListKey({
  slug,
  apiSlug,
  locationSlug,
  sort,
  filters,
  endpoint,
}: {
  slug: string;
  apiSlug?: string;
  locationSlug?: string;
  sort?: string;
  filters?: string;
  endpoint?: string;
}) {
  return `${slug}|${apiSlug ?? ''}|${locationSlug ?? ''}|${sort ?? ''}|${filters ?? ''}|${endpoint ?? ''}`;
}

type PaginationItem = number | 'ellipsis';

function buildPagination(current: number, total: number): PaginationItem[] {
  // Çok az sayfa varsa hepsini göster
  if (total <= 9) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }

  const pages = new Set<number>();

  // İlk 3
  pages.add(1);
  pages.add(2);
  pages.add(3);

  // Son 3
  pages.add(total);
  pages.add(total - 1);
  pages.add(total - 2);

  // Mevcut sayfa ve yakınları
  pages.add(current);
  pages.add(current - 1);
  pages.add(current + 1);

  // 1..total aralığına sıkıştır
  const sorted = Array.from(pages)
    .filter((p) => p >= 1 && p <= total)
    .sort((a, b) => a - b);

  const result: PaginationItem[] = [];
  let prev: number | null = null;

  for (const p of sorted) {
    if (prev !== null && p - prev > 1) {
      result.push('ellipsis');
    }
    result.push(p);
    prev = p;
  }

  return result;
}

function useGridColumns() {
  const gridRef = useRef<HTMLDivElement | null>(null);
  const [cols, setCols] = useState(4);

  useLayoutEffect(() => {
    const el = gridRef.current;
    if (!el) return;
    const update = () => {
      const style = getComputedStyle(el);
      const template = style.getPropertyValue('grid-template-columns');
      const count = template.split(' ').filter(Boolean).length || 1;
      setCols(count);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return { gridRef, cols };
}

export default function CategoryInfiniteContainer(props: Props) {
  const {
    title,
    slug,
    apiSlug,
    locationSlug,
    category,
    initial,
    total,
    sort,
    filters,
    pageSize = 24,
    showTitle = false,
    endpoint = '/api/slug-products',

    initialOffset,
    initialPrevOffset,
    initialFirstPage,
    initialCurrentPage,

    productGridClassName,
    productGridItemClassName,
    cardMaxWidth,
    cardClassName,
    cardImageClassName,
  } = props;

  const requestSlug = apiSlug || slug;

  const listKey = useMemo(
    () => makeListKey({ slug, apiSlug: requestSlug, locationSlug, sort, filters, endpoint }),
    [slug, requestSlug, locationSlug, sort, filters, endpoint],
  );

  const [all, setAll] = useState<Product[]>(initial ?? []);
  const [offset, setOffset] = useState<number>(
    typeof initialOffset === 'number' ? initialOffset : (initial?.length ?? 0),
  );
  const [prevOffset, setPrevOffset] = useState<number | null>(
    typeof initialPrevOffset === 'number' ? initialPrevOffset : null,
  );
  const [loading, setLoading] = useState(false);
  const [totalState, setTotalState] = useState<number>(total);

  const [firstPage, setFirstPage] = useState<number>(initialFirstPage ?? 1);
  const [currentPage, setCurrentPage] = useState<number>(
    initialCurrentPage ?? initialFirstPage ?? 1,
  );
  const [initialised, setInitialised] = useState(false);

  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const idSetRef = useRef<Set<number>>(new Set((initial ?? []).map((p) => Number(p.id))));
  const ioRef = useRef<IntersectionObserver | null>(null);
  const pageIoRef = useRef<IntersectionObserver | null>(null);

  const { gridRef, cols } = useGridColumns();

  const hasMoreForward = offset < totalState;
  const hasPrev = firstPage > 1 && prevOffset !== null && prevOffset >= 0;

  const urlBase = useMemo(() => {
    const origin = typeof window !== 'undefined' ? window.location.origin : 'https://dummy.local';
    const u = new URL(endpoint, origin);
    u.searchParams.set('slug', requestSlug);
    u.searchParams.set('limit', String(pageSize));
    if (sort) u.searchParams.set('sort', sort);
    if (filters) u.searchParams.set('filters', filters);
    if (locationSlug) u.searchParams.set('locationSlug', locationSlug);
    return u;
  }, [endpoint, requestSlug, sort, filters, pageSize, locationSlug]);

  const updateUrlOnly = (page: number) => {
    if (typeof window === 'undefined') return;

    const url = new URL(window.location.href);
    const currentParam = url.searchParams.get(pageVariableName);

    if (page > 1 && currentParam === String(page)) return;
    if (page <= 1 && !currentParam) return;

    url.searchParams.delete('page');

    if (page <= 1) {
      url.searchParams.delete(pageVariableName);
    } else {
      url.searchParams.set(pageVariableName, String(page));
    }

    const newRelative = url.pathname + (url.search || '') + url.hash;
    const nextState = { ...(window.history.state || {}) };
    window.history.replaceState(nextState, '', newRelative);
  };

  useEffect(() => {
    setAll(initial ?? []);
    idSetRef.current = new Set((initial ?? []).map((p) => Number(p.id)));

    setOffset(typeof initialOffset === 'number' ? initialOffset : (initial?.length ?? 0));
    setPrevOffset(typeof initialPrevOffset === 'number' ? initialPrevOffset : null);
    setFirstPage(initialFirstPage ?? 1);
    setCurrentPage(initialCurrentPage ?? initialFirstPage ?? 1);
    setTotalState(total);

    setInitialised(true);
  }, [
    listKey,
    total,
    pageSize,
    initial,
    initialOffset,
    initialPrevOffset,
    initialFirstPage,
    initialCurrentPage,
  ]);

  useEffect(() => {
    if (!initialised) return;
    updateUrlOnly(currentPage);
  }, [initialised, currentPage]);

  const loadMoreForward = async () => {
    if (!initialised) return;
    if (loading || !hasMoreForward) {
      console.log('[LOAD_MORE_FORWARD_SKIP]', { loading, hasMoreForward, offset, totalState });
      return;
    }

    setLoading(true);

    try {
      const u = new URL(urlBase.toString());
      u.searchParams.set('offset', String(offset));

      console.log('[LOAD_MORE_FORWARD_FETCH]', u.toString());

      const res = await fetch(u.toString());
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = (await res.json()) as {
        items: Product[];
        nextCursor: number | null;
        total: number;
      };

      if (typeof data.total === 'number') setTotalState(data.total);

      const incoming = data.items || [];
      const fresh = incoming.filter((p) => {
        const id = Number(p.id);
        if (idSetRef.current.has(id)) return false;
        idSetRef.current.add(id);
        return true;
      });

      setAll((prev) => [...prev, ...fresh]);
      const newOffset = data.nextCursor ?? data.total ?? offset + fresh.length;
      setOffset(newOffset);
    } catch (e: any) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const loadPreviousPage = async () => {
    if (!initialised) return;
    if (loading) return;
    if (!hasPrev || prevOffset === null || prevOffset < 0) {
      console.log('[LOAD_PREV_SKIP]', { hasPrev, prevOffset });
      return;
    }

    setLoading(true);

    try {
      const u = new URL(urlBase.toString());
      u.searchParams.set('offset', String(prevOffset));

      console.log('[LOAD_PREV_FETCH]', u.toString());

      const res = await fetch(u.toString());
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = (await res.json()) as {
        items: Product[];
        nextCursor: number | null;
        total: number;
      };

      if (typeof data.total === 'number') setTotalState(data.total);

      const incoming = data.items || [];
      const fresh = incoming.filter((p) => {
        const id = Number(p.id);
        if (idSetRef.current.has(id)) return false;
        idSetRef.current.add(id);
        return true;
      });

      setAll((prev) => [...fresh, ...prev]);
      setFirstPage((prev) => Math.max(prev - 1, 1));

      const newPrevOffset = prevOffset - pageSize;
      setPrevOffset(newPrevOffset >= 0 ? newPrevOffset : null);
    } catch (e: any) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!initialised) return;
    if (!sentinelRef.current) return;

    const io = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (first.isIntersecting) {
          void loadMoreForward();
        }
      },
      { rootMargin: '1000px' },
    );

    io.observe(sentinelRef.current);
    ioRef.current = io;
    return () => io.disconnect();
  }, [initialised, sentinelRef.current, hasMoreForward, urlBase, offset, pageSize]);

  useEffect(() => {
    if (!initialised) return;
    if (typeof window === 'undefined') return;
    if (!pageSize || pageSize <= 0) return;
    if (!all.length) return;

    if (pageIoRef.current) {
      pageIoRef.current.disconnect();
      pageIoRef.current = null;
    }

    const container = document.querySelector<HTMLElement>(`[data-category-slug="${slug}"]`);
    if (!container) {
      console.log('[PAGE_IO]', 'container not found');
      return;
    }

    const items = container.querySelectorAll<HTMLElement>('.product-grid-item');
    if (!items.length) {
      console.log('[PAGE_IO]', 'no product-grid-item found');
      return;
    }

    const totalPagesLoaded = Math.max(1, Math.ceil(all.length / pageSize));

    const observer = new IntersectionObserver(
      (entries) => {
        let bestPage: number | null = null;
        let bestTop = Number.POSITIVE_INFINITY;

        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const el = entry.target as HTMLElement;
          const pageAttr = el.dataset.page;
          if (!pageAttr) return;
          const p = Number(pageAttr);
          if (!Number.isFinite(p) || p < 1) return;

          const rect = el.getBoundingClientRect();
          const score = Math.abs(rect.top);
          if (score < bestTop) {
            bestTop = score;
            bestPage = p;
          }
        });

        if (bestPage != null && bestPage !== currentPage) {
          console.log('[PAGE_IO_HIT]', { bestPage });
          setCurrentPage(bestPage);
        }
      },
      {
        root: null,
        rootMargin: '0px 0px -60% 0px',
        threshold: 0,
      },
    );

    for (let localPage = 0; localPage < totalPagesLoaded; localPage++) {
      const index = localPage * pageSize;
      if (index >= items.length) break;
      const el = items[index];
      const realPage = firstPage + localPage;
      el.dataset.page = String(realPage);
      observer.observe(el);
    }

    pageIoRef.current = observer;

    return () => {
      observer.disconnect();
      pageIoRef.current = null;
    };
  }, [initialised, all.length, pageSize, slug, currentPage, firstPage]);

  useEffect(() => {
    return () => {
      if (ioRef.current) {
        ioRef.current.disconnect();
        ioRef.current = null;
      }
      if (pageIoRef.current) {
        pageIoRef.current.disconnect();
        pageIoRef.current = null;
      }
    };
  }, []);

  const skeletonCount = Math.min(cols * 2, pageSize, Math.max(0, totalState - offset));

  const totalPages = Math.max(1, Math.ceil(totalState / pageSize));
  const listUrl = slug.startsWith('/') ? slug : `/${slug}`;
  const paginationItems = buildPagination(currentPage, totalPages);

  console.log('[STATE]', {
    allLength: all.length,
    totalState,
    offset,
    prevOffset,
    hasMoreForward,
    hasPrev,
    firstPage,
    currentPage,
    skeletonCount,
  });

  return (
    <div data-category-slug={slug}>
      {hasPrev && (
        <div className="py-4 text-center">
          <button
            type="button"
            onClick={() => void loadPreviousPage()}
            disabled={loading}
            className="inline-flex items-center px-6 py-2 rounded-full border border-hzc_purple_500 text-hzc_purple_500 text-sm font-medium disabled:opacity-60"
          >
            {loading ? 'Yükleniyor…' : 'Önceki ürünleri yükle'}
          </button>
        </div>
      )}

      <ProductListSection
        title={title}
        products={all}
        category={category || undefined}
        url=""
        slug={slug}
        showMore={false}
        showTitle={showTitle}
        gridClassName={productGridClassName}
        gridItemClassName={productGridItemClassName}
        cardMaxWidth={cardMaxWidth}
        cardClassName={cardClassName}
        cardImageClassName={cardImageClassName}
      />

      {hasMoreForward && <div ref={sentinelRef} aria-hidden="true" />}

      <div className="py-8 flex flex-col items-center gap-4">
        {hasMoreForward && loading && <DotLoader className="text-hzc_purple_500" />}

        {totalPages > 1 ? (
          <nav aria-label="Sayfalar" className="flex flex-wrap items-center justify-center gap-2">
            {currentPage > 1 && (
              <a
                href={
                  currentPage - 1 === 1
                    ? listUrl
                    : `${listUrl}?${pageVariableName}=${currentPage - 1}`
                }
                className="px-4 py-2 rounded-full border border-hzc_purple_500 text-hzc_purple_500 text-sm md:text-base"
              >
                Önceki
              </a>
            )}

            {paginationItems.map((item, idx) => {
              if (item === 'ellipsis') {
                return (
                  <span
                    key={`dots-${idx}`}
                    className="px-3 py-1 text-hzc_purple_500 text-sm md:text-base select-none"
                  >
                    …
                  </span>
                );
              }

              const p = item;
              const isActive = p === currentPage;
              const href = p === 1 ? listUrl : `${listUrl}?${pageVariableName}=${p}`;

              return (
                <a
                  key={p}
                  href={href}
                  aria-current={isActive ? 'page' : undefined}
                  className={[
                    'px-4 py-2 rounded-full text-sm md:text-base',
                    isActive
                      ? 'bg-hzc_purple_500 text-white'
                      : 'border border-hzc_purple_500 text-hzc_purple_500 hover:bg-hzc_purple_500 hover:text-white transition-colors',
                  ].join(' ')}
                >
                  {p}
                </a>
              );
            })}

            {currentPage < totalPages && (
              <a
                href={`${listUrl}?${pageVariableName}=${currentPage + 1}`}
                className="px-4 py-2 rounded-full border border-hzc_purple_500 text-hzc_purple_500 text-sm md:text-base"
              >
                Sonraki
              </a>
            )}
          </nav>
        ) : (
          <DotEllipsis className="text-2xl md:text-3xl text-neutral-400 tracking-widest" />
        )}
      </div>
    </div>
  );
}
